import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { StudioPage, Panel, SectionLabel, Metric, Badge, Drawer, Loading, cx } from "./StudioShared";
import {
  ShieldCheck, Activity, AlertTriangle, AlertOctagon, Cpu, Workflow, Download, Upload,
  DollarSign, Clock, Users, Search, FileJson, FileSpreadsheet, Archive, Link2, GitBranch,
  Filter, RefreshCw, ScrollText, LayoutDashboard,
} from "lucide-react";

const inp = "bg-navy border border-white/10 px-3 py-2 text-sm text-white rounded-sm focus:border-gold focus:outline-none";
const SEV_TONE = { info: "slate", warning: "amber", error: "red", critical: "red" };
const STATUS_TONE = { success: "green", failed: "red", warning: "amber", skipped: "slate", waiting: "amber", info: "slate" };
const CAT_TONE = { Authentication: "gold", AI: "gold", Automation: "gold", Workflows: "gold", Billing: "red", System: "slate" };
const PRESETS = [["today", "Today"], ["yesterday", "Yesterday"], ["7d", "Last 7 Days"], ["month", "Last Month"], ["quarter", "Quarter"], ["year", "Year"]];
const fmt = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—");
const fmtMs = (ms) => (ms == null ? "—" : ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`);

export default function AuditCenter() {
  const [tab, setTab] = useState("dashboard");
  return (
    <StudioPage title="Audit Center" testid="audit-center-page"
      question="Enterprise Activity Intelligence — the permanent, append-only source of truth for every action in AIPOS. Who • What • When • Where • Why • How • Result • Cost."
      actions={
        <div className="flex gap-1 bg-navy border border-white/10 p-1 rounded-sm" data-testid="audit-tabs">
          {[["dashboard", "Dashboard", LayoutDashboard], ["timeline", "Timeline", ScrollText]].map(([k, l, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`audit-tab-${k}`}
              className={cx("flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm", tab === k ? "bg-gold text-navy" : "text-slate-400 hover:text-white")}><Icon size={14} /> {l}</button>
          ))}
        </div>}>
      {tab === "dashboard" ? <DashboardTab /> : <TimelineTab />}
    </StudioPage>
  );
}

function DashboardTab() {
  const [preset, setPreset] = useState("7d");
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get(`/admin/audit/dashboard?preset=${preset}`).then((r) => setD(r.data)).catch(() => setD({ cards: {}, heatmaps: {} })); }, [preset]);
  if (!d) return <Loading label="Loading intelligence…" />;
  const c = d.cards;
  const cards = [
    ["Total Events", c.total_events, ShieldCheck], ["In Range", c.events_in_range, Activity],
    ["Today", c.today_events, Clock], ["Errors", c.errors, AlertOctagon], ["Warnings", c.warnings, AlertTriangle],
    ["AI Calls", c.ai_calls, Cpu], ["Workflow Runs", c.workflow_runs, Workflow], ["Automation Runs", c.automation_runs, RefreshCw],
    ["Exports", c.exports, Download], ["Client Activity", c.client_activity, Users],
    ["Total AI Cost", `$${(c.total_ai_cost || 0).toFixed(2)}`, DollarSign], ["Avg Exec", fmtMs(c.avg_execution_ms), Clock],
  ];
  const heat = [
    ["Most Active Users", d.heatmaps.top_users], ["Most Active Departments", d.heatmaps.top_departments],
    ["Most Used Strategists", d.heatmaps.top_strategists], ["Most Used Models", d.heatmaps.top_models],
    ["Most Used Providers", d.heatmaps.top_providers], ["Most Active Missions", d.heatmaps.top_missions],
    ["Most Active Workflows", d.heatmaps.top_workflows], ["Most Active Automations", d.heatmaps.top_automations],
    ["Events by Category", d.heatmaps.by_category],
  ];
  return (
    <div>
      <div className="flex justify-end mb-4">
        <select value={preset} onChange={(e) => setPreset(e.target.value)} data-testid="audit-dash-preset" className={inp}>
          {PRESETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 mb-6" data-testid="audit-cards">
        {cards.map(([l, v, Icon]) => <Metric key={l} label={l} value={v ?? 0} icon={Icon} testid={`audit-card-${l.toLowerCase().replace(/[^a-z]/g, "-")}`} />)}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="audit-heatmaps">
        {heat.map(([title, rows]) => <HeatCard key={title} title={title} rows={rows || []} />)}
      </div>
    </div>
  );
}

function HeatCard({ title, rows }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <Panel className="p-4">
      <SectionLabel className="mb-3">{title}</SectionLabel>
      {rows.length === 0 ? <div className="text-slate-600 text-sm">No data yet.</div> : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.key}>
              <div className="flex justify-between text-xs mb-1"><span className="text-slate-300 truncate pr-2">{r.label || r.key}</span><span className="text-slate-500 shrink-0">{r.count}{r.cost ? ` · $${r.cost.toFixed(2)}` : ""}</span></div>
              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-gold/70 rounded-full" style={{ width: `${(r.count / max) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function TimelineTab() {
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [f, setF] = useState({ q: "", category: "", severity: "", status: "", preset: "" });
  const [skip, setSkip] = useState(0);
  const [openId, setOpenId] = useState(null);
  const LIMIT = 50;

  const load = useCallback(async () => {
    const p = new URLSearchParams({ skip: String(skip), limit: String(LIMIT) });
    Object.entries(f).forEach(([k, v]) => v && p.set(k, v));
    const [r, m] = await Promise.all([api.get(`/admin/audit/events?${p}`), meta ? Promise.resolve({ data: meta }) : api.get("/admin/audit/meta")]);
    setData(r.data); if (!meta) setMeta(m.data);
  }, [f, skip]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const doExport = async (fmtType) => {
    try {
      const p = new URLSearchParams({ format: fmtType });
      Object.entries(f).forEach(([k, v]) => v && p.set(k, v));
      const res = await api.get(`/admin/audit/export?${p}`, { responseType: fmtType === "csv" ? "blob" : "json" });
      const blob = fmtType === "csv" ? res.data : new Blob([JSON.stringify(res.data.events, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob); const a = document.createElement("a");
      a.href = url; a.download = `aipos_audit.${fmtType}`; a.click(); URL.revokeObjectURL(url);
      toast.success(`Exported ${fmtType.toUpperCase()}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data || !meta) return <Loading label="Loading timeline…" />;
  const events = data.events;

  return (
    <div>
      <Panel className="p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="audit-filters">
        <div className="flex items-center gap-1.5 text-slate-400 text-xs"><Filter size={13} /></div>
        <select value={f.preset} onChange={(e) => { setSkip(0); setF({ ...f, preset: e.target.value }); }} data-testid="audit-filter-preset" className={inp}>
          <option value="">All time</option>{PRESETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <select value={f.category} onChange={(e) => { setSkip(0); setF({ ...f, category: e.target.value }); }} data-testid="audit-filter-category" className={inp}>
          <option value="">All categories</option>{meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        <select value={f.severity} onChange={(e) => { setSkip(0); setF({ ...f, severity: e.target.value }); }} data-testid="audit-filter-severity" className={inp}>
          <option value="">Any severity</option>{meta.severities.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <select value={f.status} onChange={(e) => { setSkip(0); setF({ ...f, status: e.target.value }); }} data-testid="audit-filter-status" className={inp}>
          <option value="">Any status</option>{meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <div className="flex items-center gap-1.5 flex-1 min-w-[180px]"><Search size={14} className="text-slate-500" />
          <input value={f.q} onChange={(e) => { setSkip(0); setF({ ...f, q: e.target.value }); }} placeholder="Search who / what / entity / correlation…" data-testid="audit-search" className={cx(inp, "flex-1")} /></div>
        <button onClick={() => doExport("json")} data-testid="audit-export-json" className="flex items-center gap-1.5 border border-white/10 text-slate-300 hover:border-gold/50 px-3 py-2 text-xs rounded-sm"><FileJson size={13} /> JSON</button>
        <button onClick={() => doExport("csv")} data-testid="audit-export-csv" className="flex items-center gap-1.5 border border-white/10 text-slate-300 hover:border-gold/50 px-3 py-2 text-xs rounded-sm"><FileSpreadsheet size={13} /> CSV</button>
      </Panel>

      <div className="text-xs text-slate-500 mb-2" data-testid="audit-total">{data.total.toLocaleString()} events</div>

      <div className="space-y-2" data-testid="audit-timeline">
        {events.length === 0 ? <Panel className="p-8 text-center text-slate-500">No audit events match these filters.</Panel> :
          events.map((e) => (
            <button key={e.id} onClick={() => setOpenId(e.id)} data-testid={`audit-event-${e.id}`}
              className="w-full text-left flex items-start gap-3 border border-white/10 hover:border-gold/40 rounded-sm px-4 py-3 transition-colors bg-navy-light/30">
              <div className={cx("w-1.5 h-1.5 rounded-full mt-2 shrink-0", e.severity === "error" || e.severity === "critical" ? "bg-red-400" : e.severity === "warning" ? "bg-amber-400" : "bg-emerald-400")} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white text-sm font-medium">{e.action_type}</span>
                  <Badge tone={CAT_TONE[e.category] || "slate"}>{e.category}</Badge>
                  <Badge tone={STATUS_TONE[e.status] || "slate"}>{e.status}</Badge>
                  {e.archived && <Badge tone="slate">archived</Badge>}
                </div>
                <div className="text-slate-500 text-xs mt-1 truncate">
                  {e.actor_name || e.actor_email || "system"}
                  {e.entity_name ? ` · ${e.entity_name}` : ""}{e.module ? ` · ${e.module}` : ""}{e.department ? ` · ${e.department}` : ""}
                  {e.model ? ` · ${e.model}` : ""}{e.cost ? ` · $${e.cost.toFixed(4)}` : ""}
                </div>
              </div>
              <div className="text-right shrink-0 text-xs text-slate-500">{fmt(e.timestamp)}</div>
            </button>
          ))}
      </div>

      {data.total > LIMIT && (
        <div className="flex justify-center gap-2 mt-4" data-testid="audit-pagination">
          <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - LIMIT))} className="px-3 py-1.5 text-xs border border-white/10 rounded-sm text-slate-300 disabled:opacity-30">Prev</button>
          <span className="text-xs text-slate-500 self-center">{skip + 1}–{Math.min(skip + LIMIT, data.total)} of {data.total}</span>
          <button disabled={skip + LIMIT >= data.total} onClick={() => setSkip(skip + LIMIT)} className="px-3 py-1.5 text-xs border border-white/10 rounded-sm text-slate-300 disabled:opacity-30">Next</button>
        </div>
      )}

      {openId && <EventDrawer id={openId} isOwner={meta.is_owner} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}

function EventDrawer({ id, isOwner, onClose, onChanged }) {
  const [e, setE] = useState(null);
  const [trace, setTrace] = useState(null);
  useEffect(() => {
    api.get(`/admin/audit/events/${id}`).then((r) => {
      setE(r.data);
      if (r.data.correlation_id) api.get(`/admin/audit/correlation/${r.data.correlation_id}`).then((t) => setTrace(t.data)).catch(() => {});
    }).catch(() => setE(null));
  }, [id]);
  if (!e) return <Drawer open onClose={onClose} testid="audit-event-drawer" title="Audit Event"><Loading /></Drawer>;

  const archive = async () => { try { await api.post(`/admin/audit/events/${id}/archive`, { reason: "Owner archive" }); toast.success("Archived"); onChanged(); onClose(); } catch (err) { toast.error(formatApiError(err)); } };
  const restore = async () => { try { await api.post(`/admin/audit/events/${id}/restore`, {}); toast.success("Restored"); onChanged(); onClose(); } catch (err) { toast.error(formatApiError(err)); } };

  const kv = [
    ["Who", e.actor_name || e.actor_email || "system"], ["Email", e.actor_email], ["Role", e.role],
    ["When (UTC)", fmt(e.timestamp)], ["Category", e.category], ["Action", e.action_type],
    ["Status", e.status], ["Severity", e.severity], ["Module", e.module], ["Page", e.page],
    ["Department", e.department], ["Entity", `${e.entity_type || ""} ${e.entity_id || ""}`.trim()],
    ["Mission", e.mission], ["Workflow", e.workflow], ["Automation", e.automation], ["Strategist", e.strategist],
    ["Provider", e.provider], ["Model", e.model], ["Tokens", e.tokens], ["Cost", e.cost != null ? `$${e.cost}` : null],
    ["Duration", fmtMs(e.execution_ms)], ["Source", e.source], ["IP Address", e.ip_address],
    ["Session", e.session_id], ["Request ID", e.request_id], ["Reason", e.reason], ["Notes", e.notes],
  ].filter(([, v]) => v != null && v !== "");

  return (
    <Drawer open onClose={onClose} testid="audit-event-drawer" title={e.action_type} subtitle={`${e.category} · ${e.hold_status}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Badge tone={CAT_TONE[e.category] || "slate"}>{e.category}</Badge>
        <Badge tone={STATUS_TONE[e.status] || "slate"}>{e.status}</Badge>
        <Badge tone={SEV_TONE[e.severity] || "slate"}>{e.severity}</Badge>
        {e.archived && <Badge tone="slate">archived</Badge>}
      </div>

      <div className="grid grid-cols-2 gap-2" data-testid="audit-event-kv">
        {kv.map(([l, v]) => <div key={l} className="border border-white/10 rounded-sm p-2"><div className="text-[10px] uppercase tracking-wide text-slate-500">{l}</div><div className="text-slate-200 text-xs mt-0.5 break-words">{String(v)}</div></div>)}
      </div>

      {(e.before_state || e.after_state) && (
        <div className="grid grid-cols-2 gap-2">
          <JsonBlock title="Before" data={e.before_state} />
          <JsonBlock title="After" data={e.after_state} />
        </div>
      )}
      {e.metadata && Object.keys(e.metadata).length > 0 && <JsonBlock title="Metadata" data={e.metadata} />}

      <div className="border border-white/10 rounded-sm p-2.5">
        <div className="flex items-center gap-1.5 text-slate-400 text-xs mb-1"><Link2 size={12} /> Digital checksum (integrity)</div>
        <div className="text-slate-500 text-[10px] font-mono break-all" data-testid="audit-checksum">{e.checksum}</div>
      </div>

      {trace && trace.count > 1 && (
        <div>
          <SectionLabel className="mb-2 flex items-center gap-1.5"><GitBranch size={12} /> Correlation trace · {trace.count} linked events</SectionLabel>
          <div className="space-y-1" data-testid="audit-correlation-trace">
            {trace.events.map((t) => (
              <div key={t.id} className={cx("flex items-center gap-2 text-xs border rounded-sm px-2.5 py-1.5", t.id === e.id ? "border-gold/50 bg-gold/5" : "border-white/10")}>
                <Badge tone={STATUS_TONE[t.status] || "slate"}>{t.status}</Badge>
                <span className="text-slate-300 truncate">{t.action_type}</span>
                <span className="ml-auto text-slate-600">{fmt(t.timestamp)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {isOwner && (
        <div className="pt-2 border-t border-white/5">
          {e.archived
            ? <button onClick={restore} data-testid="audit-restore-btn" className="flex items-center gap-1.5 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 px-3 py-2 text-xs rounded-sm"><Archive size={13} /> Restore event</button>
            : <button onClick={archive} data-testid="audit-archive-btn" className="flex items-center gap-1.5 border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 px-3 py-2 text-xs rounded-sm"><Archive size={13} /> Archive event (append-only)</button>}
          <p className="text-[11px] text-slate-600 mt-1.5">Events are immutable and never deleted — archive only changes status. Owner action.</p>
        </div>
      )}
    </Drawer>
  );
}

function JsonBlock({ title, data }) {
  return (
    <div><SectionLabel className="mb-1">{title}</SectionLabel>
      <pre className="whitespace-pre-wrap text-[11px] text-slate-400 bg-navy border border-white/10 rounded-sm p-2.5 max-h-48 overflow-auto">{JSON.stringify(data, null, 2)}</pre>
    </div>
  );
}
